import type { AmountRange, Payment, PaymentKind, PaymentStatus } from '@klopotun/contracts';
import { formatMoney, minorUnitScale, parseCivilDate, type CivilDate } from '@klopotun/domain';

const STATUS_TEXT: Record<PaymentStatus, string> = {
  active: 'Активний',
  paused: 'На паузі',
  cancelled: 'Скасований',
};

const KIND_TEXT: Record<PaymentKind, string> = {
  auto_debit: 'Автосписання',
  manual: 'Вручну',
};

const MONTH_TEXT = [
  'січень',
  'лютий',
  'березень',
  'квітень',
  'травень',
  'червень',
  'липень',
  'серпень',
  'вересень',
  'жовтень',
  'листопад',
  'грудень',
];

const MONTHLY_INTERVAL_TEXT: Record<number, string> = {
  1: 'щомісяця',
  3: 'щокварталу',
  6: 'раз на пів року',
};

const padded = (value: number, length: number): string => String(value).padStart(length, '0');

export const VARIABLE_AMOUNT_TEXT = 'сума змінна';

const PERCENT = 100;

const SAME_AMOUNT_PERCENT = 2;

const RANGE_DASH = '–';

const NON_BREAKING_SPACE = ' ';

export const formatCivilDateForPeople = (date: CivilDate): string =>
  `${padded(date.day, 2)}.${padded(date.month, 2)}.${padded(date.year, 4)}`;

export const formatStoredDateForPeople = (value: string): string =>
  formatCivilDateForPeople(parseCivilDate(value));

export const formatMonthName = (month: number): string => MONTH_TEXT[month - 1] ?? String(month);

export const formatPaymentStatus = (status: PaymentStatus): string => STATUS_TEXT[status];

export const formatPaymentKind = (kind: PaymentKind): string => KIND_TEXT[kind];

const isAboutTheSame = (range: AmountRange): boolean =>
  (range.maxMinor - range.minMinor) * PERCENT <= range.maxMinor * SAME_AMOUNT_PERCENT;

const wholeUnits = new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 0 });

const formatRange = (range: AmountRange, currency: string): string => {
  const scale = minorUnitScale(currency);
  const lowest = wholeUnits.format(Math.floor(range.minMinor / scale));
  const highest = wholeUnits.format(Math.ceil(range.maxMinor / scale));

  return `${lowest}${RANGE_DASH}${highest}${NON_BREAKING_SPACE}${currency}`;
};

export const formatAmountRange = (range: AmountRange, currency: string): string => {
  if (isAboutTheSame(range)) {
    return formatMoney({ amountMinor: range.maxMinor, currency });
  }

  return formatRange(range, currency);
};

export const formatVariableAmount = (payment: Payment): string => {
  const { amountRange, currency } = payment;

  if (amountRange === null) {
    return VARIABLE_AMOUNT_TEXT;
  }

  return formatAmountRange(amountRange, currency);
};

export const formatPaymentAmount = (payment: Payment): string => {
  if (payment.amountMode === 'variable' || payment.amountMinor === null) {
    return formatVariableAmount(payment);
  }

  return formatMoney({ amountMinor: payment.amountMinor, currency: payment.currency });
};

export const formatRecurrence = (payment: Payment): string => {
  if (payment.intervalUnit === 'year') {
    return payment.intervalCount === 1 ? 'щороку' : `раз на ${String(payment.intervalCount)} р.`;
  }

  return (
    MONTHLY_INTERVAL_TEXT[payment.intervalCount] ?? `раз на ${String(payment.intervalCount)} міс.`
  );
};
